// FollowUpsSidebar — real open/in-progress to-dos with a due date, soonest
// first, overdue ones called out. "Open" links to the full To-Dos list
// (a real page, never a dead button) since editing happens there.
import { Link } from 'react-router-dom'
import { CheckSquare, Plus } from 'lucide-react'
import type { CrmTodo } from '../../types'
import { dueFollowUps, formatDueDate, isOverdue, todayDateString } from '../../utils/crmPipeline'

interface FollowUpsSidebarProps {
  todos: CrmTodo[]
  onNewFollowUp: () => void
}

export default function FollowUpsSidebar({ todos, onNewFollowUp }: FollowUpsSidebarProps) {
  const today = todayDateString()
  const due = dueFollowUps(todos).slice(0, 5)

  return (
    <div className="light-card">
      <div className="px-4 py-3 border-b border-sky-200/50">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <CheckSquare size={15} className="text-sky-600" />
          Follow-ups due
        </h3>
      </div>

      {due.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-gray-500">No follow-ups due right now.</p>
        </div>
      ) : (
        <ul className="divide-y divide-sky-200/30">
          {due.map((t) => {
            const overdue = isOverdue(t.due_date, today)
            const dueLabel = formatDueDate(t.due_date)
            return (
              <li key={t.todo_id} className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900">{t.title}</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    {overdue ? `Overdue — was due ${dueLabel}` : `Due ${dueLabel}`}
                  </p>
                  <Link to="/crm/todos" className="text-xs text-sky-600 hover:text-sky-700 font-medium shrink-0">
                    Open
                  </Link>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <div className="p-3 border-t border-sky-200/50">
        <button
          onClick={onNewFollowUp}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-sky-600 hover:bg-sky-500/10 rounded-lg transition-colors"
        >
          <Plus size={14} /> New follow-up to-do
        </button>
      </div>
    </div>
  )
}
